import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { CACHE_KEYS, isCacheValid } from '@/lib/cacheUtils';

interface PlanLimit {
  id: string;
  role: string;
  max_projects: number;
  max_rigs_per_project: number;
  max_ai_messages: number;
  features: string[];
  description: string | null;
  price_monthly: number;
  price_annual: number;
  is_custom_pricing: boolean;
  stripe_product_id: string | null;
  stripe_price_monthly: string | null;
  stripe_price_annual: string | null;
}

const GUEST_AI_MESSAGE_KEY = CACHE_KEYS.GUEST_AI_MESSAGES;
// IMPORTANT: Keep in sync with rig-assistant edge function GUEST_DAILY_PLAN_LIMIT
const GUEST_AI_MESSAGE_LIMIT = 3;
const GUEST_MAX_PROJECTS = 1;
const GUEST_MAX_RIGS_PER_PROJECT = 1;
const GUEST_PROJECTS_KEY = CACHE_KEYS.GUEST_PROJECTS;
const PLAN_LIMITS_CACHE_KEY = CACHE_KEYS.PLAN_LIMITS;
const PLAN_LIMITS_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours - matches CACHE_TTL.SETTINGS

// Default plan limits matching current database values (prevents flash on load)
const DEFAULT_PLAN_LIMITS: PlanLimit[] = [
  {
    id: '', role: 'user', max_projects: 1, max_rigs_per_project: 1, max_ai_messages: 5,
    features: ['basic_builder', 'save_projects'], description: 'Free tier - basic access',
    price_monthly: 0, price_annual: 0, is_custom_pricing: false,
    stripe_product_id: null, stripe_price_monthly: null, stripe_price_annual: null
  },
  {
    id: '', role: 'pro', max_projects: 5, max_rigs_per_project: 5, max_ai_messages: 50,
    features: ['basic_builder', 'save_projects', 'export_pdf', 'my_gear', 'priority_support', 'rental_pricing'],
    description: 'Pro tier - extended limits', price_monthly: 60, price_annual: 600, is_custom_pricing: false,
    stripe_product_id: 'prod_Tb0fcLbQAPPO5H', stripe_price_monthly: 'price_1SdoeKBJUfxwlWSBPwXI4o0c', stripe_price_annual: 'price_1Sdof0BJUfxwlWSBiAu1UDe9'
  },
  {
    id: '', role: 'business', max_projects: 15, max_rigs_per_project: 15, max_ai_messages: 500,
    features: ['basic_builder', 'save_projects', 'export_pdf', 'my_gear', 'priority_support', 'team_sharing', 'custom_pdf_branding', 'version_tracking', 'rental_pricing'],
    description: 'Business tier - unlimited', price_monthly: 350, price_annual: 3480, is_custom_pricing: false,
    stripe_product_id: 'prod_Tb0g3QlaeRwttR', stripe_price_monthly: 'price_1SdofBBJUfxwlWSBts35zqeg', stripe_price_annual: 'price_1SdofVBJUfxwlWSBizR5pg8x'
  },
  {
    id: '', role: 'enterprise', max_projects: -1, max_rigs_per_project: -1, max_ai_messages: -1,
    features: ['basic_builder', 'save_projects', 'export_pdf', 'my_gear', 'priority_support', 'team_sharing', 'api_access', 'custom_pdf_branding', 'version_tracking', 'rental_pricing'],
    description: null, price_monthly: 0, price_annual: 0, is_custom_pricing: true,
    stripe_product_id: null, stripe_price_monthly: null, stripe_price_annual: null
  },
];

interface CachedLimits {
  data: PlanLimit[];
  timestamp: number;
}

interface CachedLimitsResult {
  data: PlanLimit[];
  isFresh: boolean;
}

function getCachedLimits(): CachedLimitsResult {
  try {
    const cached = localStorage.getItem(PLAN_LIMITS_CACHE_KEY);
    if (cached) {
      const parsed: CachedLimits = JSON.parse(cached);
      const isFresh = Date.now() - parsed.timestamp < PLAN_LIMITS_CACHE_TTL;
      // Return cached data even if stale (for instant UI), but indicate freshness
      if (parsed.data && parsed.data.length > 0) {
        return { data: parsed.data, isFresh };
      }
    }
  } catch (e) {
    console.error('Failed to load cached plan limits:', e);
  }
  return { data: DEFAULT_PLAN_LIMITS, isFresh: false };
}

function saveCachedLimits(limits: PlanLimit[]): void {
  try {
    const cached: CachedLimits = {
      data: limits,
      timestamp: Date.now()
    };
    localStorage.setItem(PLAN_LIMITS_CACHE_KEY, JSON.stringify(cached));
  } catch (e) {
    console.error('Failed to cache plan limits:', e);
  }
}

export function clearPlanLimitsCache(): void {
  try {
    localStorage.removeItem(PLAN_LIMITS_CACHE_KEY);
  } catch (e) {
    console.error('Failed to clear plan limits cache:', e);
  }
}

// Get plan limits for display (uses 24h cache, fetches if needed)
export async function getPlanLimitsForDisplay(): Promise<PlanLimit[]> {
  const cached = getCachedLimits();
  if (cached.data.length > 0) {
    return cached.data;
  }
  
  // Fetch fresh data
  const { data } = await supabase
    .from('plan_limits')
    .select('*');
  
  if (data) {
    saveCachedLimits(data as PlanLimit[]);
    return data as PlanLimit[];
  }
  
  return [];
}

// Re-export PlanLimit type for external use
export type { PlanLimit };

interface GuestAiUsage {
  count: number;
  periodStart: string; // ISO date string for month
}

function getGuestAiUsage(): GuestAiUsage {
  try {
    const stored = localStorage.getItem(GUEST_AI_MESSAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
      if (parsed.periodStart === currentMonth) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Failed to load guest AI usage:', e);
  }
  return { count: 0, periodStart: new Date().toISOString().slice(0, 7) };
}

function saveGuestAiUsage(usage: GuestAiUsage): void {
  try {
    localStorage.setItem(GUEST_AI_MESSAGE_KEY, JSON.stringify(usage));
  } catch (e) {
    console.error('Failed to save guest AI usage:', e);
  }
}

// Calculate the AI usage period start based on user type
function getAiUsagePeriodStart(
  subscription: { isSubscribed: boolean; subscriptionStart: string | null } | null,
  profileCreatedAt: string | null
): Date {
  const now = new Date();
  
  // Paid user: use billing cycle start
  if (subscription?.isSubscribed && subscription.subscriptionStart) {
    return new Date(subscription.subscriptionStart);
  }
  
  // Free user: use registration date anniversary this month
  if (profileCreatedAt) {
    const registrationDate = new Date(profileCreatedAt);
    const dayOfMonth = registrationDate.getDate();
    
    // Get the period start based on registration day
    // If we're past the day, use this month. If before, use last month.
    const periodStart = new Date(now.getFullYear(), now.getMonth(), dayOfMonth);
    if (now < periodStart) {
      periodStart.setMonth(periodStart.getMonth() - 1);
    }
    return periodStart;
  }
  
  // Fallback: calendar month
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

export function usePlanLimits() {
  const { user, roles, isAdmin, profile, subscription } = useAuth();
  
  // Get cached data on init - start with cached permissions for instant UI
  const cachedResult = getCachedLimits();
  
  const [limits, setLimits] = useState<PlanLimit[]>(cachedResult.data);
  const [currentProjectCount, setCurrentProjectCount] = useState(0);
  const [projectRigCounts, setProjectRigCounts] = useState<Record<string, number>>({});
  const [currentAiMessageCount, setCurrentAiMessageCount] = useState(0);
  // Start with isLoading: false if we have fresh cached data
  const [isLoading, setIsLoading] = useState(!cachedResult.isFresh);

  // Fetch plan limits only if cache is stale
  useEffect(() => {
    if (cachedResult.isFresh) return; // Skip fetch if cache is fresh
    
    const fetchLimits = async () => {
      const { data } = await supabase
        .from('plan_limits')
        .select('*');
      
      if (data) {
        setLimits(data as PlanLimit[]);
        saveCachedLimits(data as PlanLimit[]);
      }
    };

    fetchLimits();
  }, []);

  // Only depend on the specific profile field we need to prevent re-fetching on unrelated profile changes
  const profileCreatedAt = profile?.created_at ?? null;

  // Fetch current usage counts
  useEffect(() => {
    const fetchUsage = async () => {
      if (!user) {
        // Load guest usage from localStorage
        try {
          const stored = localStorage.getItem(GUEST_PROJECTS_KEY);
          if (stored) {
            const localProjects = JSON.parse(stored);
            setCurrentProjectCount(localProjects.length);
            const rigCounts: Record<string, number> = {};
            for (const project of localProjects) {
              rigCounts[project.id] = project.rigs?.length || 0;
            }
            setProjectRigCounts(rigCounts);
          } else {
            setCurrentProjectCount(0);
            setProjectRigCounts({});
          }
        } catch (e) {
          setCurrentProjectCount(0);
          setProjectRigCounts({});
        }
        // Load guest AI usage from localStorage
        const guestUsage = getGuestAiUsage();
        setCurrentAiMessageCount(guestUsage.count);
        setIsLoading(false);
        return;
      }

      // Let usage data refresh silently - permissions already cached
      try {
        // Get project count
        const { count: projectCount } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);
        
        setCurrentProjectCount(projectCount || 0);

        // Get rig counts per project
        const { data: projects } = await supabase
          .from('projects')
          .select('id')
          .eq('user_id', user.id);

        if (projects) {
          const rigCounts: Record<string, number> = {};
          for (const project of projects) {
            const { count: rigCount } = await supabase
              .from('rigs')
              .select('*', { count: 'exact', head: true })
              .eq('project_id', project.id);
            
            rigCounts[project.id] = rigCount || 0;
          }
          setProjectRigCounts(rigCounts);
        }

        // Get AI message usage for authenticated users
        // Use subscription period for paid users, registration anniversary for free users
        const periodStart = getAiUsagePeriodStart(subscription, profileCreatedAt);
        
        const { data: aiUsage } = await supabase
          .from('ai_message_usage')
          .select('message_count')
          .eq('user_id', user.id)
          .gte('period_start', periodStart.toISOString());

        // Sum all matching rows in case of duplicates
        const totalCount = aiUsage?.reduce((sum, row) => sum + row.message_count, 0) ?? 0;
        setCurrentAiMessageCount(totalCount);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsage();
  }, [user, subscription, profileCreatedAt]);

  // Get the user's highest role limit
  const userLimit = useMemo(() => {
    // Priority order: admin > business > pro > moderator > user
    const rolePriority = ['admin', 'business', 'pro', 'moderator', 'user'];
    
    for (const role of rolePriority) {
      if (roles.includes(role as any) || (role === 'user' && user)) {
        const limit = limits.find(l => l.role === role);
        if (limit) return limit;
      }
    }
    
    // Default for guests - enforce limits on local storage too
    return { max_projects: GUEST_MAX_PROJECTS, max_rigs_per_project: GUEST_MAX_RIGS_PER_PROJECT };
  }, [roles, limits, user]);

  const maxProjects = userLimit.max_projects;
  const maxRigsPerProject = userLimit.max_rigs_per_project;
  
  // Check for admin override on AI messages
  const planDefaultAiMessages = user ? (userLimit as PlanLimit).max_ai_messages ?? 5 : GUEST_AI_MESSAGE_LIMIT;
  const aiMessageOverride = profile?.ai_message_limit_override;
  const maxAiMessages = (aiMessageOverride !== null && aiMessageOverride !== undefined) 
    ? aiMessageOverride 
    : planDefaultAiMessages;

  // -1 means unlimited
  const canCreateProject = isAdmin || maxProjects === -1 || currentProjectCount < maxProjects;
  
  const canCreateRig = useCallback((projectId: string) => {
    if (isAdmin || maxRigsPerProject === -1) return true;
    const currentCount = projectRigCounts[projectId] || 0;
    return currentCount < maxRigsPerProject;
  }, [isAdmin, maxRigsPerProject, projectRigCounts]);

  const getRigCount = useCallback((projectId: string) => {
    return projectRigCounts[projectId] || 0;
  }, [projectRigCounts]);

  const canSendAiMessage = useCallback(() => {
    if (isAdmin || maxAiMessages === -1) return true;
    return currentAiMessageCount < maxAiMessages;
  }, [isAdmin, maxAiMessages, currentAiMessageCount]);

  const incrementAiMessageCount = useCallback(async () => {
    if (!user) {
      // Guest user - update localStorage
      const usage = getGuestAiUsage();
      usage.count += 1;
      saveGuestAiUsage(usage);
      setCurrentAiMessageCount(usage.count);
      return;
    }

    // Get the appropriate period start for this user
    const periodStart = getAiUsagePeriodStart(subscription, profile?.created_at ?? null);

    // Authenticated user - use secure RPC function with billing period
    const { error } = await supabase.rpc('increment_ai_message_usage', {
      billing_period_start: periodStart.toISOString()
    });
    
    if (error) {
      console.error('Failed to increment AI message usage:', error);
      return;
    }
    
    // Update local state
    setCurrentAiMessageCount(prev => prev + 1);
  }, [user, subscription, profile]);

  const refreshUsage = useCallback(async () => {
    if (!user) {
      // Refresh guest project/rig counts from localStorage
      try {
        const stored = localStorage.getItem(GUEST_PROJECTS_KEY);
        if (stored) {
          const localProjects = JSON.parse(stored);
          setCurrentProjectCount(localProjects.length);
          const rigCounts: Record<string, number> = {};
          for (const project of localProjects) {
            rigCounts[project.id] = project.rigs?.length || 0;
          }
          setProjectRigCounts(rigCounts);
        } else {
          setCurrentProjectCount(0);
          setProjectRigCounts({});
        }
      } catch (e) {
        // Keep existing counts on error
      }
      // Also refresh AI usage
      const guestUsage = getGuestAiUsage();
      setCurrentAiMessageCount(guestUsage.count);
      return;
    }

    const { count: projectCount } = await supabase
      .from('projects')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id);
    
    setCurrentProjectCount(projectCount || 0);

    const { data: projects } = await supabase
      .from('projects')
      .select('id')
      .eq('user_id', user.id);

    if (projects) {
      const rigCounts: Record<string, number> = {};
      for (const project of projects) {
        const { count: rigCount } = await supabase
          .from('rigs')
          .select('*', { count: 'exact', head: true })
          .eq('project_id', project.id);
        
        rigCounts[project.id] = rigCount || 0;
      }
      setProjectRigCounts(rigCounts);
    }

    // Refresh AI message usage with user-specific period
    const periodStart = getAiUsagePeriodStart(subscription, profile?.created_at ?? null);
    
    const { data: aiUsage } = await supabase
      .from('ai_message_usage')
      .select('message_count')
      .eq('user_id', user.id)
      .gte('period_start', periodStart.toISOString());

    // Sum all matching rows in case of duplicates
    const totalCount = aiUsage?.reduce((sum, row) => sum + row.message_count, 0) ?? 0;
    setCurrentAiMessageCount(totalCount);
  }, [user, subscription, profile]);

  return {
    maxProjects,
    maxRigsPerProject,
    maxAiMessages,
    currentProjectCount,
    currentAiMessageCount,
    canCreateProject,
    canCreateRig,
    canSendAiMessage,
    getRigCount,
    incrementAiMessageCount,
    refreshUsage,
    isLoading,
    limits, // For admin panel
  };
}

// Hook for admin to manage plan limits
export function useAdminPlanLimits() {
  const [limits, setLimits] = useState<PlanLimit[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchLimits = async () => {
    setIsLoading(true);
    const { data } = await supabase
      .from('plan_limits')
      .select('*')
      .order('role');
    
    if (data) {
      // Parse features from JSON if needed
      const parsed = data.map(limit => ({
        ...limit,
        features: Array.isArray(limit.features) ? limit.features : [],
      })) as PlanLimit[];
      setLimits(parsed);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchLimits();
  }, []);

  const updateLimit = async (role: string, updates: { max_projects?: number; max_rigs_per_project?: number; max_ai_messages?: number; price_monthly?: number; price_annual?: number; is_custom_pricing?: boolean }) => {
    const { error } = await supabase
      .from('plan_limits')
      .update(updates)
      .eq('role', role as any);
    
    if (!error) {
      await fetchLimits();
    }
    return { error };
  };

  const updateFeatures = async (role: string, features: string[]) => {
    const { error } = await supabase
      .from('plan_limits')
      .update({ features })
      .eq('role', role as any);
    
    if (!error) {
      await fetchLimits();
    }
    return { error };
  };

  return {
    limits,
    isLoading,
    updateLimit,
    updateFeatures,
    refreshLimits: fetchLimits,
  };
}
